import { Spinner, StatCard } from "@enduring-systems/react-ui";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

import { Button } from "@/common/ui/button";

import type { MetricSeries, MetricsResponse } from "./health-metrics-api";
import { type TimeRange, useHealthDashboard } from "./use-health-dashboard";

const TIME_RANGE_OPTIONS: { value: TimeRange; label: string }[] = [
  { value: "1h", label: "1h" },
  { value: "24h", label: "24h" },
  { value: "7d", label: "7d" },
];

function sumPoints(series: MetricSeries | null): number {
  if (!series) return 0;
  return series.points.reduce((acc, p) => acc + p.value, 0);
}

function lastNonNullValue(series: MetricSeries | null): number | null {
  if (!series || series.points.length === 0) return null;
  for (let i = series.points.length - 1; i >= 0; i--) {
    const point = series.points[i];
    if (point !== undefined) return point.value;
  }
  return null;
}

function sparkPoints(series: MetricSeries | null): number[] | undefined {
  if (!series || series.points.length === 0) return undefined;
  return series.points.map((p) => p.value);
}

function findSeries(response: MetricsResponse | null, id: string): MetricSeries | null {
  if (!response) return null;
  return response.series.find((s) => s.id === id) ?? null;
}

interface HealthStripProps {
  baseUrl: string;
  env: string;
}

export function HealthStrip({ baseUrl, env }: HealthStripProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const { cwData, statsData, errorRate, isLoading, error, timeRange, setTimeRange } =
    useHealthDashboard(baseUrl);

  // Fail-soft: when metrics are unreachable, keep the strip visible with a
  // muted "unavailable" state. The rest of the inbox still works.
  if (error) {
    return (
      <div
        data-testid="health-strip-unavailable"
        className="flex items-center justify-between gap-3 rounded-md border border-dashed bg-card/50 px-3 py-2 text-sm text-muted-foreground"
      >
        <span>Ingest metrics unavailable for {env}.</span>
        <span className="text-xs">({error})</span>
      </div>
    );
  }

  const invocations = sumPoints(findSeries(cwData, "ingest.invocations"));
  const errors = sumPoints(findSeries(cwData, "ingest.errors"));
  const messagesReceived = sumPoints(findSeries(statsData, "inbox.messages.received"));

  const ingestDot = errors > 0 ? "bg-yellow-500" : invocations > 0 ? "bg-green-500" : "bg-gray-400";

  return (
    <div data-testid="health-strip" className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-card px-3 py-2 text-sm">
        <div className="flex flex-wrap items-center gap-4">
          <span className="flex items-center gap-2" data-testid="health-strip-ingest">
            <span
              aria-label={`Ingest status: ${ingestDot.includes("green") ? "ok" : ingestDot.includes("yellow") ? "errors" : "idle"}`}
              className={`inline-block h-2.5 w-2.5 rounded-full ${ingestDot}`}
            />
            <span className="text-muted-foreground">Ingest</span>
            <span className="font-mono">{invocations}</span>
          </span>
          <span data-testid="health-strip-errors">
            <span className="text-muted-foreground">Errors </span>
            <span className="font-mono">{errorRate ?? "—"}</span>
          </span>
          <span data-testid="health-strip-messages">
            <span className="text-muted-foreground">Messages </span>
            <span className="font-mono">{messagesReceived}</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1" role="group" aria-label="Time range">
            {TIME_RANGE_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                variant={timeRange === opt.value ? "default" : "outline"}
                size="sm"
                onClick={() => setTimeRange(opt.value)}
                aria-pressed={timeRange === opt.value}
                data-testid={`health-strip-range-${opt.value}`}
                className="h-7 px-2 text-xs"
              >
                {opt.label}
              </Button>
            ))}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="health-drawer"
            data-testid="health-strip-toggle"
            className="h-7 gap-1 px-2 text-xs"
          >
            {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {open ? "Hide details" : "Details"}
          </Button>
        </div>
      </div>

      {open && (
        <div id="health-drawer" data-testid="health-drawer" className="space-y-4 p-2">
          {isLoading ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : (
            <HealthDrawerTiles cwData={cwData} statsData={statsData} errorRate={errorRate} />
          )}
        </div>
      )}
    </div>
  );
}

interface DrawerTilesProps {
  cwData: MetricsResponse | null;
  statsData: MetricsResponse | null;
  errorRate: string | null;
}

function HealthDrawerTiles({ cwData, statsData, errorRate }: DrawerTilesProps): React.ReactElement {
  const invocations = findSeries(cwData, "ingest.invocations");
  const errorsSeries = findSeries(cwData, "ingest.errors");
  const p50 = findSeries(cwData, "ingest.duration.p50");
  const p99 = findSeries(cwData, "ingest.duration.p99");

  const nsCreated = findSeries(statsData, "inbox.namespaces.created");
  const msgsReceived = findSeries(statsData, "inbox.messages.received");
  const dkimFail = findSeries(statsData, "inbox.messages.dkim_fail");
  const spfFail = findSeries(statsData, "inbox.messages.spf_fail");

  return (
    <>
      <section aria-labelledby="drawer-lambda">
        <h3 id="drawer-lambda" className="mb-2 text-sm font-semibold">
          Lambda
        </h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          <MetricTile testId="drawer-tile-invocations" label="Invocations" series={invocations} />
          <MetricTile testId="drawer-tile-errors" label="Errors" series={errorsSeries} />
          <ErrorRateTile errorRate={errorRate} />
          <DurationTile testId="drawer-tile-p50" label="p50 duration" series={p50} />
          <DurationTile testId="drawer-tile-p99" label="p99 duration" series={p99} />
        </div>
      </section>
      <section aria-labelledby="drawer-inbox">
        <h3 id="drawer-inbox" className="mb-2 text-sm font-semibold">
          Inbox
        </h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <MetricTile
            testId="drawer-tile-ns-created"
            label="Namespaces created"
            series={nsCreated}
          />
          <MetricTile
            testId="drawer-tile-msgs-received"
            label="Messages received"
            series={msgsReceived}
          />
          <MetricTile testId="drawer-tile-dkim-fail" label="DKIM fails" series={dkimFail} />
          <MetricTile testId="drawer-tile-spf-fail" label="SPF fails" series={spfFail} />
        </div>
      </section>
    </>
  );
}

interface TileProps {
  testId: string;
  label: string;
  series: MetricSeries | null;
}

function MetricTile({ testId, label, series }: TileProps): React.ReactElement {
  const isEmpty = !series || series.points.length === 0;
  const spark = sparkPoints(series);

  if (isEmpty) {
    return (
      <div data-testid={testId} className="rounded-lg border bg-card p-3">
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
        <div className="mt-1 text-xs text-muted-foreground">No data</div>
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <StatCard label={label} value={sumPoints(series)} {...(spark ? { spark } : {})} />
    </div>
  );
}

function DurationTile({ testId, label, series }: TileProps): React.ReactElement {
  const isEmpty = !series || series.points.length === 0;
  const spark = sparkPoints(series);
  const last = lastNonNullValue(series);

  if (isEmpty || last === null) {
    return (
      <div data-testid={testId} className="rounded-lg border bg-card p-3">
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
        <div className="mt-1 text-xs text-muted-foreground">No data</div>
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <StatCard label={label} value={`${Math.round(last)} ms`} {...(spark ? { spark } : {})} />
    </div>
  );
}

function ErrorRateTile({ errorRate }: { errorRate: string | null }): React.ReactElement {
  if (errorRate === null) {
    return (
      <div data-testid="drawer-tile-error-rate" className="rounded-lg border bg-card p-3">
        <div className="text-xs font-medium text-muted-foreground">Error rate</div>
        <div className="mt-1 text-xs text-muted-foreground">No data</div>
      </div>
    );
  }
  return (
    <div data-testid="drawer-tile-error-rate">
      <StatCard label="Error rate" value={errorRate} />
    </div>
  );
}
